import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const PAGE_W = 80;
const MARGIN = 5;
const CONTENT_W = PAGE_W - MARGIN * 2;

const money = (n) => `Rs. ${(Number(n) || 0).toLocaleString('en-PK', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})}`;

// doc.text() returns the jsPDF instance, not a cursor value, so this must
// return y explicitly or the next call gets a document where a number belongs.
const line = (doc, y, char = '-') => {
  doc.text(char.repeat(32), PAGE_W / 2, y, { align: 'center' });
  return y;
};

const pair = (doc, label, value, y, opts = {}) => {
  doc.setFontSize(opts.size || 8);
  if (opts.bold) doc.setFont('helvetica', 'bold'); else doc.setFont('helvetica', 'normal');
  doc.text(label, MARGIN, y);
  doc.text(String(value ?? ''), PAGE_W - MARGIN, y, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  return y + 4.5;
};

/**
 * Builds the receipt as a PDF sized for an 80mm thermal printer.
 * Returns the jsPDF doc so callers can print or save it.
 */
export function buildReceiptPdf(transaction = {}, shop = {}) {
  const {
    items = [],
    subtotal = 0,
    discount = 0,
    tax = 0,
    total = 0,
    paymentMethod = 'Cash',
    amountTendered = 0,
    change = 0,
    shortfall = 0,
    operatorName = '',
  } = transaction;

  const id = transaction.id || transaction.transactionId || transaction.saleId || 'N/A';
  const date = transaction.date || transaction.createdAt || new Date();

  const doc = new jsPDF({ unit: 'mm', format: [PAGE_W, 240], orientation: 'portrait' });
  let y = 8;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(shop.name || 'Haider Pan Shop', PAGE_W / 2, y, { align: 'center' });
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  if (shop.address) {
    doc.text(doc.splitTextToSize(String(shop.address), CONTENT_W), PAGE_W / 2, y, { align: 'center' });
    y += 3.5 * doc.splitTextToSize(String(shop.address), CONTENT_W).length;
  }
  if (shop.phone) {
    doc.text(String(shop.phone), PAGE_W / 2, y, { align: 'center' });
    y += 4;
  }
  if (shop.taxId) {
    doc.text(`Tax ID: ${shop.taxId}`, PAGE_W / 2, y, { align: 'center' });
    y += 4;
  }

  y += 1;
  doc.setDrawColor(0);
  doc.setLineWidth(0.2);
  y = line(doc, y);
  y += 1;

  const d = new Date(date);
  doc.setFontSize(7.5);
  doc.text('Transaction', MARGIN, y);
  doc.text(String(id), PAGE_W - MARGIN, y, { align: 'right' });
  y += 4;
  doc.text(
    d.toLocaleString('en-PK', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
    PAGE_W / 2,
    y,
    { align: 'center' }
  );
  y += 5;

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    theme: 'plain',
    styles: { fontSize: 7.5, cellPadding: 1, overflow: 'linebreak' },
    headStyles: { fontStyle: 'bold', fillColor: false, textColor: 0, lineWidth: 0 },
    head: [['Item', 'Qty', 'Rate', 'Amount']],
    body: items.map((item) => {
      const qty = item.quantity || 1;
      const rate = item.salePrice || item.price || 0;
      return [
        String(item.name || item.productName || ''),
        String(qty),
        (Number(rate) || 0).toLocaleString('en-PK'),
        ((Number(rate) || 0) * qty).toLocaleString('en-PK'),
      ];
    }),
    columnStyles: {
      // autoTable only redistributes width across columns that lack a numeric
      // cellWidth, so when every column is fixed the values must sum to exactly
      // the printable width (PAGE_W - 2 * MARGIN = 70mm). Otherwise it drops
      // cells from the right and warns "... units width could not fit page".
      0: { cellWidth: 30 },
      1: { cellWidth: 8, halign: 'right' },
      2: { cellWidth: 16, halign: 'right' },
      3: { cellWidth: 16, halign: 'right' },
    },
  });

  y = doc.lastAutoTable.finalY + 2;
  y = line(doc, y);
  y += 4;

  y = pair(doc, 'Subtotal', money(subtotal), y);
  if (Number(discount) > 0) y = pair(doc, 'Discount', `- ${money(discount)}`, y);
  if (Number(tax) > 0) y = pair(doc, 'Tax', money(tax), y);
  y += 1;
  doc.setFontSize(10);
  y = pair(doc, 'Grand Total', money(total), y, { bold: true, size: 10 });
  y += 1;
  y = line(doc, y, '=');
  y += 4;

  y = pair(doc, 'Payment', String(paymentMethod || '').toUpperCase(), y);
  if (Number(amountTendered) > 0) y = pair(doc, 'Tendered', money(amountTendered), y);
  if (Number(change) > 0) y = pair(doc, 'Change', money(change), y);
  if (Number(shortfall) > 0) y = pair(doc, 'Shortfall', money(shortfall), y);
  if (operatorName) y = pair(doc, 'Served By', operatorName, y);

  y += 2;
  y = line(doc, y);
  y += 4;
  doc.setFontSize(7.5);
  doc.text('Thank you for your purchase!', PAGE_W / 2, y, { align: 'center' });

  const needed = y + 12;
  if (needed > 240) {
    doc.addPage([PAGE_W, needed], 'portrait');
  }

  return { doc, id };
}

const fileName = (id) => `receipt-${String(id).replace(/[^\w-]+/g, '_')}.pdf`;

/** Opens the browser print dialog with the receipt. */
export function printReceipt(transaction, shop) {
  const { doc, id } = buildReceiptPdf(transaction, shop);
  const url = doc.output('bloburl');

  const win = window.open(url, '_blank');
  if (!win) {
    // Popup blocked: fall back to the inline PDF print dialog.
    doc.autoPrint();
    doc.save(fileName(id));
    return;
  }
  win.focus();
  setTimeout(() => {
    try { win.print(); } catch (_) { /* user can print from the PDF viewer */ }
  }, 300);
}

/** Downloads the receipt as a PDF. */
export function saveReceiptPdf(transaction, shop) {
  const { doc, id } = buildReceiptPdf(transaction, shop);
  doc.save(fileName(id));
}
